import { useState, useCallback } from 'react'
import { useRouter } from 'next/router'
import posthog from 'posthog-js'
import { FormAuthenticate } from '@ui/blocks/authentication/form-authenticate'

export default function HomePage() {
  const router = useRouter()
  const [error, setError] = useState<string | undefined>()

  // Cached email state for multi-step flow
  const [checkedEmail, setCheckedEmail] = useState('')

  const handleCheckEmail = useCallback(async (email: string) => {
    setCheckedEmail(email)
    setError(undefined)
    try {
      const res = await fetch('/api/auth/check-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      })
      if (res.ok) {
        const data = await res.json()
        return data.kind as 'existing-with-password' | 'existing-no-password' | 'new'
      }
    } catch {
      // Fallback to OTP
    }
    return 'new' as const
  }, [])

  const handleLogin = useCallback(async (email: string, password: string) => {
    setError(undefined)
    const res = await fetch('/api/auth/login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    })
    const data = await res.json()
    if (!res.ok) {
      setError(data.error || 'Login failed')
      return false
    }
    return true
  }, [])

  const handleSendAuthCode = useCallback(async (email: string) => {
    setError(undefined)
    const res = await fetch('/api/auth/send-otp', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    })
    if (!res.ok) {
      const data = await res.json()
      setError(data.error || 'Failed to send code')
    }
  }, [])

  const handleVerifyAuthCode = useCallback(async (email: string, code: string) => {
    setError(undefined)
    const res = await fetch('/api/auth/verify-otp', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, code }),
    })
    const data = await res.json()
    if (!res.ok) {
      setError(data.error || 'Invalid code')
      return false
    }
    return true
  }, [])

  const handleCreatePassword = useCallback(async (password: string) => {
    setError(undefined)
    const res = await fetch('/api/auth/set-password', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ password }),
    })
    const data = await res.json()
    if (!res.ok) {
      if (data.details && Array.isArray(data.details)) {
        return data.details.join('. ')
      }
      return data.error || 'Failed to set password'
    }
    return true
  }, [])

  const handleAuthenticated = useCallback(() => {
    // Identify the user in PostHog using the cached email from the auth flow
    if (checkedEmail) {
      posthog.identify(checkedEmail, { email: checkedEmail })
    }
    const redirectUrl = (router.query.redirect as string) || '/'
    router.push(redirectUrl)
  }, [router, checkedEmail])

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-900">
      <div className="w-full max-w-md p-8">
        <FormAuthenticate
          accentColor="#0070f3"
          accentHoverColor="#0051b3"
          error={error}
          onCheckEmail={handleCheckEmail}
          onLogin={handleLogin}
          onSendAuthCode={handleSendAuthCode}
          onVerifyAuthCode={handleVerifyAuthCode}
          onCreatePassword={handleCreatePassword}
          onAuthenticated={handleAuthenticated}
        />
      </div>
    </div>
  )
}
